import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { Spin } from 'antd';

import LoginPage from '@/pages/auth/LoginPage';
import SidebarLayout from '@/pages/portal/SidebarLayout';
import ErrorBoundary from '@/components/ErrorBoundary';
import AdminHome from '@/pages/portal/admin/AdminHome';
import AdminUsersPage from '@/pages/portal/admin/AdminUsersPage';
import AdminLicensePlansPage from '@/pages/portal/admin/AdminLicensePlansPage';
import AdminLicenseUnitsPage from '@/pages/portal/admin/AdminLicenseUnitsPage';
import AdminDevicesPage from '@/pages/portal/admin/AdminDevicesPage';
import AdminP12CertsPage from '@/pages/portal/admin/AdminP12CertsPage';
import AdminReportsPage from '@/pages/portal/admin/AdminReportsPage';
import AdminAuditPage from '@/pages/portal/admin/AdminAuditPage';
import DistributorHome from '@/pages/portal/distributor/DistributorHome';
import DistributorReportsPage from '@/pages/portal/distributor/DistributorReportsPage';
import DistributorVendorDetail from '@/pages/portal/distributor/DistributorVendorDetail';
import VendorHome from '@/pages/portal/vendor/VendorHome';
import VendorBulkIssuePage from '@/pages/portal/vendor/VendorBulkIssuePage';
import VendorReportsPage from '@/pages/portal/vendor/VendorReportsPage';
import VendorAccountPage from '@/pages/portal/vendor/VendorAccountPage';
import { useSession } from '@/hooks/useSession';
import { roleHomePath, type SessionUser } from '@/services/session';

function LoadingGate() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Spin size="large" tip="Loading session..." />
    </div>
  );
}

function RoleShell({
  user,
  allow,
}: {
  user: SessionUser;
  allow: (u: SessionUser) => boolean;
}) {
  const location = useLocation();
  if (!allow(user)) {
    return (
      <Navigate
        to={roleHomePath(user.role)}
        replace
        state={{ from: location, deniedFor: 'role' }}
      />
    );
  }
  return (
    <SidebarLayout user={user}>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </SidebarLayout>
  );
}

export default function App() {
  const { user, loading } = useSession();

  if (loading) return <LoadingGate />;

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to={roleHomePath(user.role)} replace /> : <LoginPage />}
        />

        {user ? (
          <>
            <Route
              path="/admin"
              element={<RoleShell user={user} allow={(u) => u.organizationType === 'system'} />}
            >
              <Route index element={<AdminHome />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="licenses" element={<AdminLicensePlansPage />} />
              <Route path="license-units" element={<AdminLicenseUnitsPage />} />
              <Route path="devices" element={<AdminDevicesPage />} />
              <Route path="p12-certs" element={<AdminP12CertsPage />} />
              <Route path="reports" element={<AdminReportsPage />} />
              <Route path="audit" element={<AdminAuditPage />} />
            </Route>

            <Route
              path="/distributor"
              element={
                <RoleShell user={user} allow={(u) => u.organizationType === 'distributor'} />
              }
            >
              <Route index element={<DistributorHome />} />
              <Route path="vendors/:id" element={<DistributorVendorDetail />} />
              <Route path="reports" element={<DistributorReportsPage />} />
            </Route>

            <Route
              path="/vendor"
              element={<RoleShell user={user} allow={(u) => u.organizationType === 'vendor'} />}
            >
              <Route index element={<VendorHome />} />
              <Route path="bulk-issue" element={<VendorBulkIssuePage />} />
              <Route path="reports" element={<VendorReportsPage />} />
              <Route path="account" element={<VendorAccountPage />} />
            </Route>

            <Route path="*" element={<Navigate to={roleHomePath(user.role)} replace />} />
          </>
        ) : (
          <Route path="*" element={<Navigate to="/login" replace />} />
        )}
      </Routes>
    </BrowserRouter>
  );
}
